import React, { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva('inline-flex items-center justify-center p-1 text-muted-foreground', {
  variants: {
    variant: {
      line: 'h-11 w-full rounded-none border-b bg-transparent px-0',
      pill: 'h-10 rounded-md bg-muted',
    },
    size: {
      sm: 'text-xs',
      md: 'text-sm',
    },
  },
  defaultVariants: {
    variant: 'pill',
    size: 'md',
  },
});

interface TabsListProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(({
  className,
  variant,
  size,
  ...props
}, ref) => <TabsPrimitive.List ref={ref} className={cn(tabsListVariants({
  variant,
  size,
}), className)} {...props} />);
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva('inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 font-medium ring-offset-background transition-all duration-fast ease-standard focus-glow disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      line: 'rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none',
      pill: 'rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs',
    },
    size: {
      sm: 'text-xs',
      md: 'text-sm',
    },
  },
  defaultVariants: {
    variant: 'pill',
    size: 'md',
  },
});

interface TabsTriggerProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(({
  className,
  variant,
  size,
  ...props
}, ref) => <TabsPrimitive.Trigger ref={ref} className={cn(tabsTriggerVariants({
  variant,
  size,
}), className)} {...props} />);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const TabsContent = forwardRef<React.ElementRef<typeof TabsPrimitive.Content>, ComponentPropsWithoutRef<typeof TabsPrimitive.Content>>(({
  className,
  ...props
}, ref) => <TabsPrimitive.Content ref={ref} className={cn('mt-2 ring-offset-background focus-glow data-[state=active]:motion-safe:animate-fade-up', className)} {...props} />);
TabsContent.displayName = TabsPrimitive.Content.displayName;
export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants, tabsTriggerVariants };